import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes, { any } from 'prop-types'
import { DataGrid, useGridApiRef } from '@mui/x-data-grid'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Box, Grid } from '../../../../../components/mui/Core/index'
import LinkButton from '../../../../../components/LinkButton/link-button'
import {
  resetParentFeature,
  resetSelectedOrderItem,
  setParentFeature,
  setSelectedOrderItem,
} from '../../../../SerialNumbers/serial-numbers-slice'
import { useTheme } from '../../../../../components/mui/Styles/index'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import { parentFeatureConstants } from '../../../../../constants/parent-feature-constants'
import * as Constants from '../../../../../constants/en.json'

export function WmsOrderDetailsTable({ filterBy, filterValue, searchByOptions }) {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const gridApiRef = useGridApiRef()
  const shipmentItemDetails = useSelector((state) => state.orderDetails.shipmentItemDetails)
  const isFetchingShipmentDetails = useSelector(
    (state) => state.orderDetails.isFetchingShipmentDetails,
  )
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue
  const gridStyle = {
    minHeight: '160px',
    height: isFetchingShipmentDetails ? '400px' : 'auto',
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
  }

  const handleMultipleLinkClick = (item) => {
    dispatch(resetSelectedOrderItem())
    dispatch(setSelectedOrderItem(item))
    dispatch(resetParentFeature())
    dispatch(setParentFeature(parentFeatureConstants.ORDERS))
    navigate('/serialnumbers')
  }

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  const rows = shipmentItemDetails.map((row) => ({
    id: row.index,
    itemNumber: row.itemNumber,
    itemDescription: row.itemDescription,
    lotNumber: row.lotNumber,
    serialNumbers: row.serialNumbers,
    expirationDate: row.expirationDate,
    trackingNumber: row.trackingNumber,
    trackingURL: row.trackingURL,
    orderQty: row.orderQty,
    shippedQty: row.shippedQty,
    orderUOM: row.orderUOM,
    itemHoldCode: row.itemHoldCode,
  }))

  const columns = [
    {
      field: 'id',
      headerName: Constants.orders.order_details.INDEX,
      flex: 0.3,
      minWidth: 85,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'itemNumber',
      headerName: Constants.orders.order_details.NDC_ITEM,
      flex: 0.5,
      minWidth: 130,
    },
    {
      field: 'itemDescription',
      headerName: Constants.orders.order_details.ITEM_DESCRIPTION,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'lotNumber',
      headerName: Constants.orders.order_details.LOT_NUMBER,
      flex: 0.5,
      minWidth: 120,
      valueGetter: (value) => {
        return value || Constants.orders.order_details.NONE
      },
    },
    {
      field: 'serialNumbers',
      headerName: Constants.orders.order_details.SERIAL_NUMBERS,
      flex: 0.5,
      minWidth: 150,
      valueGetter: (value) => {
        const serialNumbers = value
        if (serialNumbers?.length === 0) {
          return Constants.orders.order_details.NONE
        }
        if (serialNumbers.length > 1) {
          return Constants.orders.order_details.MULTIPLE
        }
        return value
      },
      renderCell: (cellValues) => {
        const serialNumbers = cellValues.row?.serialNumbers
        if (serialNumbers?.length === 0) {
          return Constants.orders.order_details.NONE
        }
        if (serialNumbers.length === 1) {
          return serialNumbers[0]
        }
        if (serialNumbers.length > 1) {
          return (
            <LinkButton
              handleLinkClick={() => handleMultipleLinkClick(cellValues.row)}
              linkText='Multiple'
            />
          )
        }
        return cellValues
      },
    },
    {
      field: 'expirationDate',
      headerName: Constants.orders.order_details.EXPIRATION_DATE,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) =>
        (value && moment.utc(value).format('L')) || Constants.orders.order_details.NONE,
      flex: 0.5,
      minWidth: 110,
    },
    {
      field: 'trackingNumber',
      headerName: Constants.orders.order_details.TRACKING_NUMBER,
      flex: 0.7,
      minWidth: 180,
      renderCell: (cellValues) => {
        const trackingNumber = cellValues.row.trackingNumber
        const trackingURL = cellValues.row.trackingURL
        if (
          cellValues.row?.trackingNumber !== Constants.orders.order_details.PENDING &&
          cellValues.row?.trackingNumber !== Constants.orders.order_details.UNAVAILABLE
        ) {
          return (
            <a target='_blank' rel='noopener noreferrer' href={trackingURL}>
              {trackingNumber}
            </a>
          )
        }
        return trackingNumber
      },
    },
    {
      field: 'shippedQty',
      headerAlign: 'right',
      align: 'right',
      headerName: Constants.orders.order_details.SHIP_QTY,
      flex: 0.7,
      minWidth: 110,
      valueGetter: (value, row) => {
        return `${row.shippedQty} / ${row.orderQty}`
      },
    },
    {
      field: 'orderUOM',
      headerName: Constants.orders.order_details.ORDER_UOM,
      flex: 0.4,
      minWidth: 57,
    },
    {
      field: 'itemHoldCode',
      headerName: Constants.orders.order_details.HOLD_CODE,
      flex: 0.6,
      minWidth: 125,
    },
  ]

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid sx={{ paddingTop: 3 }}>
        <DataGrid
          apiRef={gridApiRef}
          sx={gridStyle}
          rows={rows}
          columns={columns}
          loading={isFetchingShipmentDetails}
          filterModel={filterModel}
          disableColumnFilter
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          pageSizeOptions={[25, 50]}
          slotProps={{
            pagination: {
              component: 'div',
              showLastButton: true,
              showFirstButton: true,
            },
            noResultsOverlay: {
              filterByDescription,
              noResultsValue,
            },
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
          slots={{
            noResultsOverlay: NoResultsFoundTableOverlay,
          }}
          disableRowSelectionOnClick
        />
      </Grid>
    </Box>
  )
}

WmsOrderDetailsTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
}

export default WmsOrderDetailsTable
